import type { StoreCheckHit } from '@/bt-common/scheduling/send-protocol.types';

/** The fields of a `getUnloggedWaMessageKeyIfExistInStore` result that make a store-check hit. */
export interface StoreLookupKeyAndAck {
    waMessageKey?: string | null;
    ack?: number;
}

/**
 * #590 H13 — the one place a store lookup becomes a bt-common `StoreCheckHit`, so the retry
 * thunk's GATE 1 and the send protocol's store adapter (GATE 2) judge the same copy the same way
 * through `classifyStoreHit`. An absent ack reads as PENDING (0): a copy is not proof of delivery
 * unless WhatsApp acked it. The page lookup always returns an ack with a key; the default covers
 * the wire contract's optional field.
 */
export function toStoreCheckHit(result: StoreLookupKeyAndAck | undefined, onWire = false): StoreCheckHit | null {
    if (!result?.waMessageKey) return null;
    return { waMessageKey: result.waMessageKey, ack: result.ack ?? 0, ...(onWire ? { onWire: true } : {}) };
}

/** The slice of a page send-trace snapshot that wire evidence is read from. */
export interface WireEvidenceTrace {
    entries: ReadonlyArray<{ stage: string }>;
}

/** Trace event the media send path marks once the upload is done and the message is handed back to WhatsApp's own send queue. */
const MEDIA_WIRE_HANDOFF_EVENT = '@media:msg-updated';

/**
 * #658 — did the last send attempt get PAST the upload and hand the message to WhatsApp's send queue?
 *
 * A store copy at ack 0 alone is not proof of that: an outbox entry whose upload never finished is
 * also at ack 0, and re-sending it is correct. Only the hand-off event on the page trace proves WhatsApp
 * took over (sendQueue → encrypt → stanza), where a re-send would duplicate. No trace (page reloaded, bridge
 * timed out) is no evidence, so the hit stays `unsent` exactly as before.
 */
export function traceShowsWireHandoff(trace: WireEvidenceTrace | null | undefined): boolean {
    return !!trace?.entries.some((e) => e.stage === MEDIA_WIRE_HANDOFF_EVENT);
}

/**
 * #658 — durable wire evidence: the message doc itself already records this very copy as sent-pending-ack.
 * That status with a key is only ever written after a send returned the key or a maybe-sent hit was recorded,
 * so it survives a page reload that wipes the page trace. The key must match the copy now found.
 */
export function docRecordsPendingAckCopy(
    doc: { status?: unknown; waMessageKey?: unknown } | null | undefined,
    foundKey: string | null | undefined,
): boolean {
    return !!foundKey && doc?.status === 'sent_pending_ack' && doc.waMessageKey === foundKey;
}
